import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Stack,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  ArrowForward, 
  AutoAwesome, 
  Search,
  FilterList,
  Palette,
  Science,
  Public,
  RocketLaunch,
  Clear
} from '@mui/icons-material';
import { NICHE_AGENTS } from '../models/nicheAgents';
import { NEW_NICHE_AGENTS } from '../models/newNicheAgents';

const NicheAgentsGallery: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Combine both sets of agents
  const allAgents = [...NICHE_AGENTS, ...NEW_NICHE_AGENTS.map(agent => ({
    id: agent.id,
    name: agent.name,
    endpoint: agent.endpoint,
    description: agent.description,
    color: agent.color,
    subAgents: [],
    isNew: true
  }))];

  const agentCategories = [
    {
      name: 'Creative & Design',
      icon: <Palette />,
      color: '#e91e63',
      agents: ['architecture', 'culinary', 'textile', 'botanical', 'soundscape']
    },
    {
      name: 'Science & Engineering',
      icon: <Science />,
      color: '#2196f3',
      agents: ['mechanical', 'aerospace', 'quantumphysics', 'medical', 'neurological', 'stock-photo-technology']
    },
    {
      name: 'Earth & Environment',
      icon: <Public />,
      color: '#4caf50',
      agents: ['environment', 'marine', 'geological', 'climate', 'urban']
    },
    {
      name: 'Space & Discovery',
      icon: <RocketLaunch />,
      color: '#9c27b0',
      agents: ['astronomical', 'archaeological', 'forensic']
    },
    {
      name: 'Urban Culture & Movement',
      icon: <AutoAwesome />,
      color: '#ff5722',
      agents: ['breakdance', 'culture-disco', 'urban-bike-auto-life']
    }
  ];

  const getCategoryForAgent = (agentId: string) => {
    return agentCategories.find(category => category.agents.includes(agentId));
  };

  const filteredAgents = allAgents.filter(agent => {
    const matchesSearch = !searchQuery || 
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.subAgents && agent.subAgents.some(sub => 
        sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.description.toLowerCase().includes(searchQuery.toLowerCase())
      ));
    
    const matchesCategory = !selectedCategory || 
      getCategoryForAgent(agent.id)?.name === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" sx={{ mb: 6 }}>
        <Typography variant="h3" gutterBottom>
          Niche Agent Gallery
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Explore our collection of 22 specialized AI agents, including 4 new agents
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Chip 
            icon={<AutoAwesome />} 
            label="18 Agents with 180 Sub-Agents" 
            color="primary" 
          />
          <Chip 
            icon={<AutoAwesome />} 
            label="4 New Specialized Agents" 
            color="secondary" 
          />
        </Stack>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search agents, sub-agents, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label="All Categories"
                onClick={() => setSelectedCategory(null)}
                color={!selectedCategory ? 'primary' : 'default'}
                variant={!selectedCategory ? 'filled' : 'outlined'}
              />
              {agentCategories.map((category) => (
                <Chip
                  key={category.name}
                  icon={category.icon}
                  label={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  color={selectedCategory === category.name ? 'primary' : 'default'}
                  variant={selectedCategory === category.name ? 'filled' : 'outlined'}
                  sx={{ 
                    borderColor: category.color,
                    '&.MuiChip-colorPrimary': {
                      backgroundColor: category.color
                    }
                  }}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>
        {(searchQuery || selectedCategory) && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Found {filteredAgents.length} agents
          </Typography>
        )}
      </Paper>

      {/* Display filtered agents grouped by category */}
      {agentCategories.map((category) => {
        const categoryAgents = filteredAgents.filter(agent => 
          category.agents.includes(agent.id)
        );
        
        if (categoryAgents.length === 0) return null;
        
        return (
          <Box key={category.name} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Badge 
                badgeContent={categoryAgents.length} 
                color="primary"
                sx={{ '& .MuiBadge-badge': { backgroundColor: category.color } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {category.icon}
                  <Typography variant="h4">
                    {category.name}
                  </Typography>
                </Box>
              </Badge>
            </Box>
            <Grid container spacing={3}>
              {categoryAgents.map((agent) => (
                <Grid item xs={12} sm={6} md={4} key={agent.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Box 
                      sx={{ 
                        height: 8, 
                        backgroundColor: agent.color || '#1976d2' 
                      }} 
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="h5">
                          {agent.name}
                        </Typography>
                        {(agent as any).isNew && (
                          <Chip label="NEW" size="small" color="secondary" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {agent.description}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                        {agent.subAgents && agent.subAgents.length > 0 && (
                          <Chip 
                            label={`${agent.subAgents.length} Sub-Agents`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                        <Chip 
                          label={agent.endpoint.replace('/api/', '')} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </Stack>
                    </CardContent>
                    <CardActions>
                      <Button 
                        fullWidth
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={() => {
                          if ((agent as any).isNew) {
                            navigate(`/new-niche-agents/${agent.id}`);
                          } else {
                            navigate(`/niche-agents/${agent.id}`);
                          }
                        }}
                        sx={{ backgroundColor: agent.color }}
                      >
                        Launch Agent
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      <Paper sx={{ p: 4, mt: 6, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h5" gutterBottom>
          About Niche Agents
        </Typography>
        <Typography variant="body1" paragraph>
          Each niche agent is equipped with 10 specialized sub-agents that provide deep domain expertise. 
          You can iterate through individual sub-agents for targeted refinements or use bulk operations 
          to enhance your prompts comprehensively.
        </Typography>
        <Typography variant="body2">
          All agents support session persistence, prompt compaction, and temperature control for optimal results.
        </Typography>
      </Paper>
    </Container>
  );
};

export default NicheAgentsGallery;